import { useUpdateConversationMessageRatingMutation } from "#features/conversations/api/useUpdateConversationMessageRatingMutation.ts";
import { AssistantMessageToolbarButton } from "#features/conversations/components/conversation-view/AssistantMessageToolbarButton.tsx";
import { RatingModal } from "#features/conversations/components/conversation-view/RatingModal.tsx";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import type { ConversationMessage, Rating } from "#types/types.ts";
import { IconThumbsDown, IconThumbsDownFill } from "@intentui/icons";
import classNames from "classnames";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { toast } from "react-toastify";

import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import "./AssistantMessageRatingButton.css";

interface Props {
    conversationId: string;
    message: ConversationMessage;
    rating: Rating;
}

interface RatingContent {
    icon: ReactNode;
    tooltip: string;
}

export const AssistantMessageRatingButton = ({ conversationId, message, rating }: Props) => {
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const { mutate } = useUpdateConversationMessageRatingMutation();
    const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);

    const { id, rating: currentRating } = message;

    const isSelected = useMemo(() => rating === currentRating, [currentRating, rating]);

    const setRating = useCallback(() => {
        const newRating = rating === currentRating ? undefined : rating;
        const trackingValue = newRating ? rating : "null";

        trackEvent(MixpanelEvent.AssistantResponseRated, { Value: trackingValue, "Conversation Id": conversationId, "Message Id": id });
        mutate({
            conversationId,
            conversationMessageId: id,
            rating: newRating
        }, {
            onError: () => (toast.error(tShared("errors.updateRating")))
        });

        if (newRating && rating === "bad") {
            setIsRatingModalOpen(true);
        }
    }, [conversationId, currentRating, id, mutate, rating, tShared, trackEvent]);

    const ratingContent:Record<Rating, RatingContent> = useMemo(() => {
        const ThumbIcon = isSelected ? IconThumbsDownFill : IconThumbsDown;
        const animatedClassName = { "wai-assistant-message-rating-icon-animated": isSelected };

        return {
            good: {
                icon: <span className={classNames("wai-assistant-message-rating-icon-up", animatedClassName)}><ThumbIcon /></span>,
                tooltip: tShared("helpfulResponse")
            },
            bad: {
                icon: <ThumbIcon className={classNames(animatedClassName)} />,
                tooltip: tShared("unhelpfulResponse")
            }
        };
    }, [isSelected, tShared]);

    const { icon, tooltip } = ratingContent[rating];

    return (
        <>
            <AssistantMessageToolbarButton
                aria-label={tooltip}
                aria-pressed={isSelected}
                isSelected={isSelected}
                onPress={setRating}
                tooltip={tooltip}
            >
                {icon}
            </AssistantMessageToolbarButton>
            <RatingModal
                key={message.rating}
                conversationId={conversationId}
                message={message}
                isOpen={isRatingModalOpen}
                closeRatingModal={() => setIsRatingModalOpen(false)}
            />
        </>
    );
};
